import { useCallback, useEffect, useState } from "react";
import { Loader2, MoreHorizontal, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { AddAdminDialog, TempPasswordDialog, ConfirmDialog } from "@/components/team/TeamDialogs";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Pill = ({ on, children, testid }) => (
  <span data-testid={testid} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${on ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}>{children}</span>
);

const ACTION = { added: "Added", role_changed: "Role changed", deactivated: "Deactivated", reactivated: "Reactivated", removed: "Removed", password_reset: "Password reset" };

const RowMenu = ({ a, onAct }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" data-testid={`team-actions-${a.email}`}><MoreHorizontal /></Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onSelect={() => onAct("role", a)} data-testid={`team-role-${a.email}`}>{a.role === "super_admin" ? "Change to Admin" : "Change to Super admin"}</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => onAct("reset", a)} data-testid={`team-reset-${a.email}`}>Reset password</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => onAct(a.is_active ? "deactivate" : "reactivate", a)} data-testid={`team-toggle-active-${a.email}`}>{a.is_active ? "Deactivate" : "Reactivate"}</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-destructive" onSelect={() => onAct("remove", a)} data-testid={`team-remove-${a.email}`}>Remove</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const TeamLog = ({ items }) => (
  <div className="mt-10 rounded-lg border bg-card" data-testid="team-log">
    <div className="border-b px-6 py-4"><h3 className="text-lg font-semibold">Team activity</h3></div>
    {!items.length && <div className="px-6 py-8 text-center text-sm text-muted-foreground">No team changes yet.</div>}
    <ul className="divide-y">
      {items.map((l, i) => (
        <li key={i} className="flex flex-wrap items-baseline justify-between gap-2 px-6 py-3" data-testid={`team-log-${i}`}>
          <span className="text-sm"><b>{ACTION[l.action] || l.action}</b> · {l.target_name} <span className="text-muted-foreground">({l.target_email})</span>{l.details && <span className="text-muted-foreground"> — {l.details}</span>}</span>
          <span className="num text-xs text-muted-foreground">{fmtDateTime(l.at)} ET · by {l.actor_name}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Team() {
  const [items, setItems] = useState(null);
  const [log, setLog] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [reset, setReset] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => {
    api.get("/team").then((r) => setItems(r.data.items));
    api.get("/team/log").then((r) => setLog(r.data.items));
  }, []);
  useEffect(load, [load]);

  const run = async (kind, a) => {
    const calls = {
      role: () => api.put(`/team/${a.id}/role`, { role: a.role === "super_admin" ? "admin" : "super_admin" }),
      deactivate: () => api.post(`/team/${a.id}/deactivate`), reactivate: () => api.post(`/team/${a.id}/reactivate`),
      remove: () => api.delete(`/team/${a.id}`),
    };
    try {
      await calls[kind]();
      toast.success({ role: "Role updated", deactivate: `${a.name} deactivated`, reactivate: `${a.name} reactivated`, remove: `${a.name} removed` }[kind]);
      load();
    } catch (e) {
      toast.error(apiError(e).message);
    }
  };
  const onAct = (kind, a) => {
    if (kind === "reset") return setReset(a);
    if (kind === "remove" || kind === "deactivate") return setConfirm({ kind, a });
    return run(kind, a);
  };

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow="Super admin" title="Team" subtitle="Admins who can access the lending workspace."
        actions={<Button onClick={() => setAddOpen(true)} data-testid="add-admin-button"><UserPlus /> Add admin</Button>} />
      <div className="overflow-x-auto rounded-lg border bg-card">
        <Table data-testid="team-table">
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last login</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {!items && <TableRow><TableCell colSpan={6} className="py-10 text-center"><Loader2 className="mx-auto animate-spin" /></TableCell></TableRow>}
            {items?.map((a) => (
              <TableRow key={a.id} data-testid={`team-row-${a.email}`}>
                <TableCell className="font-semibold">{a.name}{a.is_self && <span className="ml-2 text-xs font-normal text-muted-foreground">(you)</span>}</TableCell>
                <TableCell>{a.email}</TableCell>
                <TableCell data-testid={`team-role-label-${a.email}`}>{a.role_label}</TableCell>
                <TableCell><Pill on={a.is_active} testid={`team-status-${a.email}`}>{a.is_active ? "Active" : "Deactivated"}</Pill></TableCell>
                <TableCell className="num text-sm text-muted-foreground" data-testid={`team-last-login-${a.email}`}>{a.last_login_at ? `${fmtDateTime(a.last_login_at)} ET` : "Never"}</TableCell>
                <TableCell className="text-right"><RowMenu a={a} onAct={onAct} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TeamLog items={log} />
      <AddAdminDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <TempPasswordDialog admin={reset} onClose={() => setReset(null)} onSaved={load} />
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} onConfirm={(c) => run(c.kind, c.a)} />
    </div>
  );
}
